import {
  Button,
  HStack,
  Field,
  RadioGroup,
  Stack,
  Input,
  ButtonGroup,
  SimpleGrid,
  NativeSelect,
  Tag,
  Text,
  Box,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { t } from "i18next";
import { useStores } from "../../../../shared/stores/RootStore";
import { CustomDialog } from "../../../../shared/views/CustomDialog";
import { useState } from "react";
import { RadioOptions } from "../../../../hla/views/HlaWayDetail/form/RadioOptions";
import {
  generateOptions,
  SelectOption,
} from "../../../../hla/views/HlaWayDetail/form/utils";
import {
  FacilityType,
  FacilityWidth,
  FlowDirectionType,
  HeavyVehicleFlowType,
  NumberOfLanesType,
} from "@keita/lanepatrol-sdk";
import { PresenceDistance } from "../../../../hla/models/HlaLane";
import { CloseIcon } from "../../../../shared/views/icons";
import { HlaWayType } from "../../../../hla/models/HlaWay";

const AddHlaForecastActionModalBase = ({
  onClose,
}: {
  onClose: () => void;
}) => {
  const { projectStore, hlaForecastStore } = useStores();

  const [form, setForm] = useState<Record<string, string | null>>({});

  const handleChange = (attributeName: string, value: string | null) => {
    setForm((prev) => ({
      ...prev,
      [attributeName]: value,
    }));
  };

  const handleSubmit = () => {
    hlaForecastStore.addAction(form);
    onClose();
  };

  const clearField = (attributeName: string) =>
    setForm((prev) => {
      delete prev[attributeName];
      return { ...prev };
    });

  return (
    <CustomDialog
      size="xl"
      onClose={onClose}
      header={"Apply forecast action to filtered segments"}
      footer={
        <ButtonGroup colorPalette="primary">
          <Button variant="ghost" onClick={onClose}>
            {t(`shared:close`)}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={Object.keys(form).length === 0}
          >
            Confirm
          </Button>
        </ButtonGroup>
      }
    >
      <Stack gap={4}>
        <HStack wrap={"wrap"}>
          {Object.entries(form).map(([attributeName, value]) => {
            const [name, side] = attributeName.split(".");
            return (
              <Tag.Root key={attributeName} colorPalette={"primary"}>
                <Tag.Label>
                  {t(`shared:hlaAttribute.${name}`, {
                    unit: projectStore.speedUnitStr,
                  })}
                  {side && ` (${side})`}: {value ?? "null"}
                </Tag.Label>

                <Tag.CloseTrigger onClick={() => clearField(attributeName)}>
                  <CloseIcon />
                </Tag.CloseTrigger>
              </Tag.Root>
            );
          })}
        </HStack>

        <SimpleGrid columns={{ lg: 1, xl: 2 }} gap={4}>
          <Item label={t(`shared:hlaAttribute.type`)}>
            <NativeSelect.Root size="sm" fontSize={"sm"}>
              <NativeSelect.Field
                value={form.type ?? ""}
                onChange={(e) => {
                  const newValue = e.target.value;

                  handleChange("type", newValue === "" ? null : newValue);
                }}
              >
                {Object.values(HlaWayType).map((option) => (
                  <option value={option} key={option}>
                    {t(`shared:hlaWayType.${option}`)}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Item>

          <RadioItem
            attributeName="heavyVehicleFlow"
            options={generateOptions(Object.values(HeavyVehicleFlowType))}
            {...{ form, handleChange }}
          />

          <Item
            label={t(`shared:hlaAttribute.averageRoadSpeed`, {
              unit: projectStore.speedUnitStr,
            })}
          >
            <Input
              size={"sm"}
              fontSize={"sm"}
              type="number"
              step={10}
              onChange={(e) => {
                const { value: newValue } = e.target;
                handleChange(
                  "averageRoadSpeed",
                  newValue === "" ? null : newValue,
                );
              }}
              min={0}
              value={form.averageRoadSpeed ?? ""}
            />
          </Item>

          <Item label={t(`shared:hlaAttribute.averageDailyTraffic`)}>
            <Input
              size={"sm"}
              fontSize={"sm"}
              type="number"
              step={1000}
              onChange={(e) => {
                const { value: newValue } = e.target;
                handleChange(
                  "averageDailyTraffic",
                  newValue === "" ? null : newValue,
                );
              }}
              min={0}
              value={form.averageDailyTraffic ?? ""}
            />
          </Item>

          <RadioItem
            attributeName="lit"
            options={booleanStrOptions}
            {...{ form, handleChange }}
          />

          <RadioItem
            attributeName="facilityType"
            options={generateOptions(Object.values(FacilityType))}
            {...{ form, handleChange }}
          />

          <RadioItem
            attributeName="flowDirection"
            options={generateOptions(Object.values(FlowDirectionType))}
            {...{ form, handleChange }}
          />

          <RadioItem
            attributeName="facilityWidthPerDirection"
            options={generateOptions(Object.values(FacilityWidth))}
            {...{ form, handleChange }}
          />

          <RadioItem
            attributeName="tramOrTrainRails"
            options={booleanStrOptions}
            {...{ form, handleChange }}
          />

          <RadioItem
            attributeName="lightSegregation"
            options={booleanStrOptions}
            {...{ form, handleChange }}
          />
        </SimpleGrid>

        <Box h={2}></Box>

        <AdjacentItem
          attributeName="hardSegregation"
          options={booleanStrOptions}
          {...{ form, handleChange }}
        />

        <AdjacentItem
          attributeName="roadLane"
          options={presenceDistanceOptions}
          {...{ form, handleChange }}
        />

        <AdjacentItem
          attributeName="numberOfLanes"
          options={generateOptions(Object.values(NumberOfLanesType))}
          {...{ form, handleChange }}
        />

        <AdjacentItem
          attributeName="vehicleParking"
          options={presenceDistanceOptions}
          {...{ form, handleChange }}
        />

        <AdjacentItem
          attributeName="sidewalk"
          options={presenceDistanceOptions}
          {...{ form, handleChange }}
        />

        <AdjacentItem
          attributeName="objectOrLevelChange"
          options={presenceDistanceOptions}
          {...{ form, handleChange }}
        />

        <AdjacentItem
          attributeName="severeHazard"
          options={presenceDistanceOptions}
          {...{ form, handleChange }}
        />
      </Stack>
    </CustomDialog>
  );
};

export const booleanStrOptions = [
  { value: "false", label: "Not present" },
  { value: "true", label: "Present" },
];

const presenceDistanceOptions = [
  { value: "null", label: "Not present" },
  { value: PresenceDistance.From0to1m, label: "0 to 1m" },
  { value: PresenceDistance.From1to3m, label: "1 to 3m" },
];

export const AddHlaForecastActionModal = observer(
  AddHlaForecastActionModalBase,
);

const Item = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => {
  return (
    <HStack>
      <Field.Root>
        <Field.Label>{label}</Field.Label>
        {children}
      </Field.Root>
    </HStack>
  );
};

const RadioItem = ({
  attributeName,
  form,
  options,
  handleChange,
}: {
  options: SelectOption[];
  attributeName: string;
  form: Record<string, string | null>;
  handleChange: (attributeName: string, value: string | null) => void;
}) => {
  var strValue = form[attributeName];
  if (form[attributeName] === null) {
    strValue = "null";
  }
  if (form[attributeName] === undefined) {
    strValue = "";
  }

  return (
    <Item label={t(`shared:hlaAttribute.${attributeName}`)}>
      <RadioGroup.Root
        colorPalette={"primary"}
        value={strValue}
        onValueChange={({ value }) => {
          handleChange(attributeName, value === "null" ? null : value);
        }}
      >
        <RadioOptions options={options} />
      </RadioGroup.Root>
    </Item>
  );
};

const AdjacentItem = ({
  attributeName,
  form,
  options,
  handleChange,
}: {
  options: SelectOption[];
  attributeName: string;
  form: Record<string, string | null>;
  handleChange: (attributeName: string, value: string | null) => void;
}) => {
  const leftName = `${attributeName}.left`;
  const rightName = `${attributeName}.right`;

  var valueLeft = valueToString(form[leftName]);
  var valueRight = valueToString(form[rightName]);

  return (
    <HStack justifyContent={"center"}>
      <RadioGroup.Root
        colorPalette={"primary"}
        value={valueLeft}
        onValueChange={({ value }) => {
          handleChange(leftName, value === "null" ? null : value);
        }}
      >
        <RadioOptions options={options} reverse />
      </RadioGroup.Root>

      <Text textStyle={"label"} w="140px" textAlign={"center"}>
        {t(`shared:hlaAttribute.${attributeName}`)}
      </Text>

      <RadioGroup.Root
        colorPalette={"primary"}
        value={valueRight}
        onValueChange={({ value }) => {
          handleChange(rightName, value === "null" ? null : value);
        }}
      >
        <RadioOptions options={options} />
      </RadioGroup.Root>
    </HStack>
  );
};

const valueToString = (formValue: string | undefined | null) => {
  if (formValue === null) {
    return "null";
  }
  if (formValue === undefined) {
    return "";
  }

  return formValue;
};
